/** @format */

import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { QuestionForm } from "../../components/QuestionForm";
import { getQuestion, updateQuestion, searchQuestions, getQuestionChildren } from "../../api/questions";
import {
	Question,
	Aggregations,
	CreateQuestionInput,
	getParentQuestionNumber,
	flattenTree,
} from "@question-bank/shared";
import { useBrand } from "../../hooks/useBrand";
import { paperIdOf } from "../../utils/attachments";
import { notify } from "../../components/Toast/toast";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { Locale } from "../../features/i18n/init";

const emptyAggregations: Aggregations = {
	subject: [],
};

export const AdminEditQuestionPage = () => {
	const { brand } = useBrand();

	const { id } = useParams();
	const { locale } = useParams<{ locale: Locale }>();

	useDocumentTitle(id ? `Admin – Edit Question ${id}` : "Admin – Edit Question");

	const [currentQuestion, setCurrentQuestion] = useState<Question>();
	const [siblings, setSiblings] = useState<Question[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>();
	const [isLoadingAggregations, setIsLoadingAggregations] = useState<boolean>(true);

	const navigate = useNavigate();

	// Seeded questions store their paper link in `parentQuestionId`, not a
	// `paperId` field, so derive it for the form's paper selector (QB2-223).
	const formValues = (
		currentQuestion ? { ...currentQuestion, paperId: currentQuestion.paperId ?? paperIdOf(currentQuestion) } : undefined
	) as Partial<CreateQuestionInput> | undefined;

	useEffect(() => {
		if (!id) {
			return;
		}
		const fetchID = async () => {
			try {
				setIsLoading(true);
				const response = await getQuestion(brand, id);
				setCurrentQuestion(response);

				if (response.parentQuestionId) {
					try {
						const group = await getQuestionChildren(brand, response.parentQuestionId);
						setSiblings(flattenTree(group.items).filter((item) => item.id !== response.id));
					} catch {
						setSiblings([]);
					}
				}

				setIsLoading(false);
			} catch (error) {
				if (error instanceof Error) {
					if (error.message === "Question not found") {
						notify("error", "This question does not exist");
						setError("Question not found");
					} else {
						notify("error", "Failed to update question. Please try again");
					}
				}
				setIsLoading(false);
			}
		};
		fetchID();
	}, [id, brand]);

	const [aggregations, setAggregations] = useState<Aggregations>(emptyAggregations);
	useEffect(() => {
		const fetchAggregations = async () => {
			const response = await searchQuestions(brand, {});
			setAggregations(response.aggregations ?? emptyAggregations);
			setIsLoadingAggregations(false);
		};
		fetchAggregations();
	}, [brand]);

	const onSubmit = async (data: CreateQuestionInput) => {
		if (!id) {
			return;
		}

		await updateQuestion(id, brand, data);
		// Publishing (or saving changes to a published question) returns to the
		// list; a plain draft "Save" keeps the author on the question.
		if (data.status === "published") {
			navigate(`/${locale}/admin/questions`);
		}
	};

	return (
		<div>
			{currentQuestion?.deletedAt && (
				<p className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 mb-4">
					This question has been deleted, but editing is allowed
				</p>
			)}
			{error && (
				<p className="text-red-600 text-md mb-2">
					{error} <Link to={`/${locale}/admin/questions`}>Back</Link>
				</p>
			)}
			{isLoading || isLoadingAggregations ? (
				<p>Loading...</p>
			) : (
				<>
					<QuestionForm
						defaultLanguage="en"
						aggregations={aggregations}
						isDisabled={true}
						onSubmit={onSubmit}
						onSuccess={() => {
							// Stay on the question after a draft save; the success toast confirms it.
						}}
						draftButtonLabelKey="questionForm.saveButton"
						successMessage="Question updated successfully"
						errorMessage="Failed to update question. Please try again"
						initialValues={formValues}
						hasParts={siblings.some(
							(sibling) => getParentQuestionNumber(sibling.questionNumber) === currentQuestion?.questionNumber,
						)}
					/>

					{siblings.length > 0 && (
						<div className="mt-6 rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
							<h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
								Related Parts ({siblings.length})
							</h3>
							<ul className="divide-y divide-slate-100">
								{siblings.map((part) => (
									<li key={part.id} className="flex items-center justify-between py-2 text-sm">
										<span className="font-medium text-slate-800">
											{part.questionNumber}
											{typeof part.maxMarks === "number" ? ` · ${part.maxMarks} marks` : ""}
										</span>
										<Link
											to={`/${locale}/admin/questions/${part.id}/edit`}
											className="text-xs font-semibold text-(--admin-color-primary-strong) hover:underline"
										>
											Edit
										</Link>
									</li>
								))}
							</ul>
						</div>
					)}
				</>
			)}
		</div>
	);
};
